import React, { useState, useEffect, useCallback } from 'react';

const DEFAULTS = { fontSize: 0, contrast: 'normal', linkHighlight: false, animations: true };

export default function AccessibilityToolbar() {
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState(() => {
        try {
            const saved = localStorage.getItem('a11y_settings');
            return saved ? JSON.parse(saved) : DEFAULTS;
        } catch { return DEFAULTS; }
    });

    const apply = useCallback((s) => {
        const root = document.documentElement;
        // Font size offset (-2, -1, 0, +1, +2) → scale factor
        const scale = 1 + (s.fontSize * 0.15);
        root.style.setProperty('--a11y-font-scale', scale);
        root.style.fontSize = `${scale * 100}%`;

        // Contrast modes
        root.classList.remove('a11y-high-contrast', 'a11y-dark', 'a11y-light');
        if (s.contrast === 'high') root.classList.add('a11y-high-contrast');
        else if (s.contrast === 'dark') root.classList.add('a11y-dark');
        else if (s.contrast === 'light') root.classList.add('a11y-light');

        // Link highlighting
        if (s.linkHighlight) root.classList.add('a11y-link-highlight');
        else root.classList.remove('a11y-link-highlight');

        // Animations
        if (!s.animations) root.classList.add('a11y-no-animations');
        else root.classList.remove('a11y-no-animations');
    }, []);

    useEffect(() => { apply(settings); }, [settings, apply]);

    const update = (key, value) => {
        const next = { ...settings, [key]: value };
        setSettings(next);
        localStorage.setItem('a11y_settings', JSON.stringify(next));
    };

    const reset = () => {
        setSettings(DEFAULTS);
        localStorage.setItem('a11y_settings', JSON.stringify(DEFAULTS));
    };

    return (
        <>
            {/* Global A11y Styles */}
            <style>{`
                .a11y-high-contrast { filter: contrast(1.4) !important; }
                .a11y-high-contrast * { border-color: #000 !important; }
                .a11y-dark { background-color: #1a1a2e !important; color: #e0e0e0 !important; }
                .a11y-dark header, .a11y-dark footer, .a11y-dark main { background-color: #1a1a2e !important; color: #e0e0e0 !important; }
                .a11y-dark .bg-white, .a11y-dark .bg-slate-50, .a11y-dark .bg-gray-50 { background-color: #16213e !important; color: #e0e0e0 !important; }
                .a11y-dark img { opacity: 0.85; }
                .a11y-light { background-color: #fffff0 !important; }
                .a11y-light *, .a11y-light header, .a11y-light footer, .a11y-light main { background-color: #fffff0 !important; color: #222 !important; }
                .a11y-link-highlight a { outline: 2px solid #FFD700 !important; outline-offset: 2px; text-decoration: underline !important; }
                .a11y-no-animations *, .a11y-no-animations *::before, .a11y-no-animations *::after { 
                    animation-duration: 0s !important; transition-duration: 0s !important; 
                }
            `}</style>

            {/* Toggle Button - positioned on left side */}
            <button
                onClick={() => setOpen(!open)}
                className="fixed top-1/3 left-0 z-[150] bg-blue-700 hover:bg-blue-800 text-white w-11 h-11 rounded-r-xl shadow-lg flex items-center justify-center transition-all hover:w-12 group"
                aria-label="נגישות"
                title="הגדרות נגישות"
            >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 7h-6l-2 11-2-11H5a1 1 0 0 1 0-2h6.268a3 3 0 0 0 1.464 0H18.5a1 1 0 0 1 .5 1.866V9z"/>
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" opacity="0.3"/>
                </svg>
            </button>

            {/* Panel */}
            {open && (
                <div className="fixed top-0 left-0 bottom-0 z-[160] flex">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />

                    {/* Sidebar */}
                    <div
                        className="relative w-80 max-w-[85vw] bg-white shadow-2xl flex flex-col overflow-y-auto"
                        dir="rtl"
                        style={{ animation: 'slideRight 0.3s ease-out' }}
                    >
                        <style>{`
                            @keyframes slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }
                        `}</style>

                        {/* Header */}
                        <div className="sticky top-0 bg-blue-700 text-white p-5 flex items-center justify-between z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm9 7h-6l-2 11-2-11H5a1 1 0 0 1 0-2h6.268a3 3 0 0 0 1.464 0H18.5a1 1 0 0 1 .5 1.866V9z"/>
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="font-black text-base">הגדרות נגישות</h2>
                                    <p className="text-[10px] text-blue-200 font-medium">בהתאם לתקן ישראלי 5568</p>
                                </div>
                            </div>
                            <button onClick={() => setOpen(false)} className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-lg font-bold transition-colors">✕</button>
                        </div>

                        {/* Content */}
                        <div className="p-5 space-y-5 flex-1">

                            {/* Font Size */}
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-lg">🔤</span>
                                    <h3 className="font-bold text-sm text-slate-800">גודל גופן</h3>
                                </div>
                                <div className="flex items-center gap-2 justify-center">
                                    <button
                                        onClick={() => update('fontSize', Math.max(-2, settings.fontSize - 1))}
                                        disabled={settings.fontSize <= -2}
                                        className="w-10 h-10 rounded-xl bg-white border-2 border-slate-200 hover:border-blue-400 text-xl font-black text-slate-600 hover:text-blue-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
                                    >
                                        א-
                                    </button>
                                    <div className="flex-1 text-center">
                                        <div className="text-2xl font-black text-blue-700">{settings.fontSize === 0 ? 'רגיל' : (settings.fontSize > 0 ? `+${settings.fontSize}` : settings.fontSize)}</div>
                                        <div className="text-[9px] text-slate-400 font-bold">{Math.round((1 + settings.fontSize * 0.15) * 100)}%</div>
                                    </div>
                                    <button
                                        onClick={() => update('fontSize', Math.min(2, settings.fontSize + 1))}
                                        disabled={settings.fontSize >= 2}
                                        className="w-10 h-10 rounded-xl bg-white border-2 border-slate-200 hover:border-blue-400 text-xl font-black text-slate-600 hover:text-blue-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center shadow-sm"
                                    >
                                        א+
                                    </button>
                                </div>
                            </div>

                            {/* Color & Contrast */}
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-lg">🎨</span>
                                    <h3 className="font-bold text-sm text-slate-800">צבעים וניגודיות</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: 'normal', label: 'רגיל', bg: 'bg-white', text: 'text-slate-800', border: 'border-slate-200' },
                                        { key: 'high', label: 'ניגודיות גבוהה', bg: 'bg-yellow-300', text: 'text-black', border: 'border-yellow-400' },
                                        { key: 'dark', label: 'רקע כהה', bg: 'bg-slate-900', text: 'text-white', border: 'border-slate-700' },
                                        { key: 'light', label: 'רקע בהיר', bg: 'bg-yellow-50', text: 'text-slate-900', border: 'border-yellow-200' },
                                    ].map(mode => (
                                        <button
                                            key={mode.key}
                                            onClick={() => update('contrast', mode.key)}
                                            className={`${mode.bg} ${mode.text} border-2 ${settings.contrast === mode.key ? 'ring-2 ring-blue-500 ring-offset-2 ' + mode.border : mode.border} rounded-xl p-3 text-xs font-bold transition-all hover:scale-[1.03]`}
                                        >
                                            {mode.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Link Highlighting */}
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                <button
                                    onClick={() => update('linkHighlight', !settings.linkHighlight)}
                                    className="w-full flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">🔗</span>
                                        <h3 className="font-bold text-sm text-slate-800">הדגשת קישורים</h3>
                                    </div>
                                    <div className={`w-11 h-6 rounded-full transition-colors relative ${settings.linkHighlight ? 'bg-blue-600' : 'bg-slate-300'}`}>
                                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${settings.linkHighlight ? 'right-0.5' : 'right-[22px]'}`} />
                                    </div>
                                </button>
                            </div>

                            {/* Disable Animations */}
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
                                <button
                                    onClick={() => update('animations', !settings.animations)}
                                    className="w-full flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">✨</span>
                                        <h3 className="font-bold text-sm text-slate-800">אנימציות</h3>
                                    </div>
                                    <div className={`w-11 h-6 rounded-full transition-colors relative ${settings.animations ? 'bg-blue-600' : 'bg-slate-300'}`}>
                                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${settings.animations ? 'right-0.5' : 'right-[22px]'}`} />
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
                            <button
                                onClick={reset}
                                className="w-full bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-600 font-bold text-xs py-2.5 rounded-xl transition-colors"
                            >
                                ↩ איפוס להגדרות ברירת מחדל
                            </button>
                            <p className="text-[9px] text-slate-400 text-center mt-2 font-medium">
                                תקן נגישות ישראלי SI 5568 | WCAG 2.1 AA
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
